import type { Id, StreamingSettings } from '../../core/model';
import type { RuntimeState, StudioApi } from '../../engine/api';
import type { LinkStatus } from '../../net/api';
import { attempt, safely } from '../safe';
import type { UiStore } from '../uiStore';
import { downloadBlob, pickFiles, safeFileName, timestampForFile } from './files';

export interface OutputActions {
  isStreaming(): boolean;
  isRecording(): boolean;
  toggleStreaming(): void;
  toggleRecording(): void;
  togglePause(): void;
  toggleStudioMode(): void;
  transition(): void;
  screenshot(): void;
  selectSceneByIndex(index: number): void;
  addScene(): Id | undefined;
  removeScene(sceneId: Id): void;
  save(): void;
  newCollection(): void;
  duplicateCollection(): void;
  renameCollection(): void;
  deleteCollection(id: Id, name: string): void;
  openCollection(id: Id): void;
  importCollection(): void;
  exportCollection(includeAssets: boolean): void;
}

/** Status of the output the streaming mode selects. */
export function linkStatusOf(mode: StreamingSettings['mode'], runtime: Pick<RuntimeState, 'broadcast' | 'whip' | 'swarm'>): LinkStatus {
  if (mode === 'whip') return runtime.whip.status;
  if (mode === 'swarm') return runtime.swarm.status;
  return runtime.broadcast.status;
}

export function createOutputActions(studio: StudioApi, ui: UiStore): OutputActions {
  const runtime = () => studio.runtime.getState();
  const project = () => studio.project.getState().project;
  const call = <T>(work: () => T | Promise<T>, prefix?: string) => void safely(studio, work, prefix);

  const streamingMode = () => project().output.streaming.mode;
  const linkStatus = () => linkStatusOf(streamingMode(), runtime());
  const isStreaming = () => linkStatus() !== 'idle' && linkStatus() !== 'error';
  const isRecording = () => {
    const status = runtime().recording.status;
    return status === 'recording' || status === 'paused' || status === 'starting';
  };

  /** Tracks an output call in the ui store so its buttons stay disabled until it settles. */
  const tracked = (kind: 'streaming' | 'recording', work: () => Promise<unknown>, prefix: string): void => {
    ui.getState().setBusy({ [kind]: true });
    void safely(studio, work, prefix).finally(() => ui.getState().setBusy({ [kind]: false }));
  };

  /** Going live is when background alerts start to matter; ask once per session, never in the way. */
  const offerAlerts = (): void => {
    const state = ui.getState();
    // Embedded in the portal the browser refuses notification permission to the frame; do not offer it.
    if (state.alertOffer !== 'unseen' || !state.prefs.offerAlerts || state.embedded) return;
    if (typeof Notification === 'undefined' || Notification.permission !== 'default') return;
    state.setAlertOffer('open');
  };

  const confirmThen = (enabled: boolean, title: string, message: string, onConfirm: () => void): void => {
    if (!enabled) onConfirm();
    else ui.getState().openDialog({ type: 'confirm', title, message, confirmLabel: 'Stop', danger: true, onConfirm });
  };

  const promptName = (title: string, initial: string, onSubmit: (name: string) => void): void =>
    ui.getState().openDialog({ type: 'prompt', title, label: 'Name', initial, onSubmit });

  return {
    isStreaming,
    isRecording,

    toggleStreaming() {
      if (ui.getState().busy.streaming) return;
      if (!isStreaming()) {
        offerAlerts();
        tracked('streaming', () => studio.startBroadcast(), 'Could not start streaming');
        return;
      }
      confirmThen(ui.getState().prefs.confirmStopStream, 'Stop streaming', 'Viewers will be disconnected.', () =>
        tracked('streaming', () => studio.stopBroadcast(), 'Could not stop streaming'),
      );
    },

    toggleRecording() {
      if (ui.getState().busy.recording) return;
      if (!isRecording()) {
        offerAlerts();
        tracked('recording', () => studio.startRecording(), 'Could not start recording');
        return;
      }
      confirmThen(ui.getState().prefs.confirmStopRecording, 'Stop recording', 'The recording will be finalised.', () =>
        tracked(
          'recording',
          async () => {
            const result = await studio.stopRecording();
            studio.notify('info', `Recording saved: ${result.fileName}`);
          },
          'Could not stop recording',
        ),
      );
    },

    togglePause() {
      const status = runtime().recording.status;
      call(() => {
        if (status === 'recording') studio.pauseRecording();
        else if (status === 'paused') studio.resumeRecording();
      });
    },

    toggleStudioMode: () => call(() => studio.setStudioMode(!runtime().studioMode)),
    transition: () => call(() => studio.transition(), 'Transition failed'),

    screenshot() {
      call(async () => {
        const blob = await studio.screenshot({ type: 'program' });
        downloadBlob(blob, `Screenshot ${timestampForFile(new Date())}.png`);
      }, 'Screenshot failed');
    },

    selectSceneByIndex(index) {
      const id = project().sceneOrder[index];
      if (id) call(() => studio.selectScene(id));
    },

    addScene() {
      const id = attempt(studio, () => studio.commands.addScene());
      if (id) call(() => studio.selectScene(id));
      return id;
    },

    removeScene(sceneId) {
      const scene = project().scenes[sceneId];
      if (!scene) return;
      const remove = (): void => void attempt(studio, () => studio.commands.removeScene(sceneId));
      if (!ui.getState().prefs.confirmRemoveScene) remove();
      else {
        ui.getState().openDialog({
          type: 'confirm',
          title: 'Remove scene',
          message: `Remove "${scene.name}"? Sources used only by this scene are removed with it.`,
          confirmLabel: 'Remove',
          danger: true,
          onConfirm: remove,
        });
      }
    },

    save: () => call(() => studio.save(), 'Save failed'),

    newCollection: () =>
      promptName('New scene collection', 'Untitled', (name) =>
        call(() => studio.newCollection(name), 'Could not create the collection'),
      ),

    duplicateCollection: () =>
      promptName('Duplicate scene collection', `${runtime().collection.name} copy`, (name) =>
        call(() => studio.duplicateCollection(name), 'Could not duplicate the collection'),
      ),

    renameCollection: () =>
      promptName('Rename scene collection', runtime().collection.name, (name) => {
        attempt(studio, () => studio.commands.renameProject(name));
      }),

    deleteCollection(id, name) {
      ui.getState().openDialog({
        type: 'confirm',
        title: 'Delete scene collection',
        message: `Delete "${name}" permanently? This cannot be undone.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => call(() => studio.deleteCollection(id), 'Could not delete the collection'),
      });
    },

    openCollection: (id) => call(() => studio.openCollection(id), 'Could not open the collection'),

    importCollection() {
      call(async () => {
        const [file] = await pickFiles('.json,.zip,.ohmyobs,application/json,application/zip');
        if (file) await studio.importCollection(file);
      }, 'Import failed');
    },

    exportCollection(includeAssets) {
      call(async () => {
        const blob = await studio.exportCollection({ includeAssets });
        const extension = blob.type.includes('json') ? 'json' : 'ohmyobs';
        downloadBlob(blob, `${safeFileName(runtime().collection.name)}.${extension}`);
      }, 'Export failed');
    },
  };
}
